import React from 'react';
import { useSelector } from 'react-redux';
import { twMerge } from 'tailwind-merge';

import { Telemetry } from '@videoblocks/kafka-rest-client';

import { SearchFilterContentTypes } from '../../Shared/enums';
import SearchFilterOptions from '../entities/SearchFilterOptions';
import {
  selectIsConversationalSearchCalloutVisible,
  selectSearchFilterOptions,
  selectSearchTerm,
} from '../selectors/searchSelectors';

const CONV_SEARCH_PATH = '/ai/conv-search';
const CONV_SEARCH_NEW_PATH = '/ai/conv-search/new';

// Card title text color (#1F2830) and the card background / button gradient are
// applied inline because the JIT compiler is disabled, so arbitrary-value
// Tailwind utilities (e.g. text-[#1F2830]) are not generated.
const TEXT_COLOR = '#1F2830';
const CARD_BACKGROUND = 'rgba(255, 229, 251, 0.4)';
const AI_GRADIENT =
  'radial-gradient(128.88% 1501.49% at -11.8% 100%, #9952E0 0%, #FF66E6 100%)';

function ConversationalSearchCallout(): React.ReactElement | null {
  const isVisible = useSelector(selectIsConversationalSearchCalloutVisible);
  const { contentType } = useSelector(selectSearchFilterOptions);
  const searchTerm = useSelector(selectSearchTerm);

  const contentClass = SearchFilterOptions.getContentClassFromContentType(
    contentType as SearchFilterContentTypes
  );

  if (!isVisible) {
    return null;
  }

  const handleTryNow = (): void => {
    void Telemetry.increment('conversational_search_callout_button_click');

    const trimmedSearchTerm = searchTerm?.trim();

    if (!trimmedSearchTerm) {
      window.location.href = CONV_SEARCH_PATH;
      return;
    }

    const url = new URL(CONV_SEARCH_NEW_PATH, window.location.origin);
    const params = new URLSearchParams({ prompt: trimmedSearchTerm });
    if (contentClass) {
      params.set('content-class', contentClass);
    }
    url.search = params.toString();
    window.location.href = url.toString();
  };

  return (
    <div
      className="flex flex-col items-center self-stretch gap-2.5 pt-4 px-4 pb-2 rounded-2xl"
      style={{ background: CARD_BACKGROUND }}
      data-cy="conversationalSearchCallout"
    >
      <div className="flex flex-row items-center self-stretch gap-2">
        <span
          className="flex-grow text-sm font-semibold"
          style={{ color: TEXT_COLOR, lineHeight: '20px' }}
        >
          Can&rsquo;t Find What You Need?
        </span>
      </div>

      <p
        className="self-stretch m-0 text-xs font-normal"
        style={{ color: TEXT_COLOR, lineHeight: '18px' }}
      >
        Try describing what you&rsquo;re looking for using Conversational Search
        to drill down to the right asset faster.
      </p>

      <button
        type="button"
        onClick={handleTryNow}
        className={twMerge(
          'flex flex-row justify-center items-center self-stretch gap-2',
          'py-1 px-6 rounded-lg cursor-pointer border-0 text-white text-xs font-semibold'
        )}
        style={{ background: AI_GRADIENT, height: '34px', lineHeight: '18px' }}
        aria-label="Try Conversational Search"
        data-cy="conversationalSearchCalloutButton"
      >
        <svg
          width={16}
          height={16}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <circle cx="11" cy="11" r="8" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
          <line x1="11" y1="8" x2="11" y2="14" />
          <line x1="8" y1="11" x2="14" y2="11" />
        </svg>
        Try Now
      </button>
    </div>
  );
}

export default ConversationalSearchCallout;
